import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ENV_CONFIG, API_GATEWAY_URL } from '../../config/env';
import { NetworkHelper } from '../http/networkHelper';
import { ApiResponse, ApiRequestOptions } from '../../domain/models/api';
import { attachAuthInterceptors } from './authInterceptor';

enum Environment {
  DEV = 'dev',
  TEST = 'test',
  PROD = 'prod'
}

export class ApiService {
  private static instance: ApiService;
  private client: AxiosInstance = axios.create();
  private environment: Environment = ENV_CONFIG.isDevelopment ? Environment.DEV :
    ENV_CONFIG.isTest ? Environment.TEST : Environment.PROD;
  private baseUrl: string = '';
  private defaultHeaders: Record<string, string>;
  private timeout: number = NetworkHelper.getApiConfig().apiTimeout;

  private constructor() {
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    this.setEnvironment(this.environment);

    NetworkHelper.setupNetworkConfig();
    NetworkHelper.logNetworkConfig();
  }

  /**
   * Singleton instance al
   */
  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  // Environment ayarla
  setEnvironment(env: Environment): void {
    this.environment = env;

    this.baseUrl = API_GATEWAY_URL;

    this.timeout = NetworkHelper.getApiConfig().apiTimeout;

    this.client = axios.create({
      baseURL: this.baseUrl,
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    this.client.interceptors.request.use(
      (config) => {
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response) => response,
      (error) => Promise.reject(error)
    );

    // Web login session (D9) — 401-triggered refresh-and-retry.
    // Bearer-token attachment happens via `defaultHeaders` (see setAuthToken()
    // below), not a request interceptor: `defaultHeaders` is already merged
    // into every request in `request()`, so setAuthToken() being called at
    // login/refresh/app-boot time (application/services/AuthService.ts) is
    // sufficient to keep it current on every subsequent call.
    attachAuthInterceptors(this.client);

    console.log(`[API] Ortam değiştirildi: ${env}, Base URL: ${this.baseUrl}, Timeout: ${this.timeout}ms`);
  }

  getEnvironment(): Environment {
    return this.environment;
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  setBaseUrl(url: string): void {
    this.baseUrl = url;
    if (this.client) {
      this.client.defaults.baseURL = url;
    }
  }

  setDefaultHeaders(headers: Record<string, string>): void {
    this.defaultHeaders = { ...this.defaultHeaders, ...headers };
  }

  setAuthToken(token: string): void {
    this.defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  clearAuthToken(): void {
    delete this.defaultHeaders['Authorization'];
  }

  public async post<T = any>(
    endpoint: string,
    data?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const options: ApiRequestOptions = { headers };
    return this.request<T>('POST', endpoint, data, options);
  }

  public async get<T = any>(
    endpoint: string,
    params?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const options: ApiRequestOptions = { headers, params };
    return this.request<T>('GET', endpoint, undefined, options);
  }

  public async put<T = any>(
    endpoint: string,
    data?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const options: ApiRequestOptions = { headers };
    return this.request<T>('PUT', endpoint, data, options);
  }

  public async delete<T = any>(
    endpoint: string,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const options: ApiRequestOptions = { headers };
    return this.request<T>('DELETE', endpoint, undefined, options);
  }

  async patch<T = any>(
    endpoint: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', endpoint, data, options);
  }

  async getOAuth2Token<T = any>(
    endpoint: string,
    params: Record<string, string>,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const oauth2Options: ApiRequestOptions = {
      ...options,
      headers: {
        ...options.headers,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      params,
    };
    return this.request<T>('POST', endpoint, null, oauth2Options);
  }

  private async request<T = any>(
    method: string,
    endpoint: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    try {
      let headers = { ...this.defaultHeaders, ...options.headers };


      const config: AxiosRequestConfig = {
        url: endpoint,
        method: method,
        headers: headers,
        timeout: options.timeout || this.timeout,
        baseURL: this.baseUrl,
      };

      const platformConfig = NetworkHelper.getPlatformSpecificFetchConfig();

      if (platformConfig.headers) {
        config.headers = {
          ...config.headers,
          ...platformConfig.headers
        };
      }

      if (config.headers) {
        config.headers['X-Environment'] = ENV_CONFIG.appEnv;
      }

      if (options.params) {
        config.params = options.params;
      }

      if (data) {
        config.data = data;
      }

      try {
        const response: AxiosResponse = await this.client.request(config);

        if (response.status === 204) {
          return {
            success: true,
            data: null,
            statusCode: 204,
          };
        }

        return {
          success: true,
          data: response.data,
          statusCode: response.status,
        };
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;

          if (NetworkHelper.getApiConfig().enableDebug) {
            console.error('[ApiService] Request failed:', {
              url: axiosError.config?.url,
              method: axiosError.config?.method,
              status: axiosError.response?.status,
              statusCode: axiosError.code
            });
          }

          const statusCode = axiosError.response?.status || 0;
          const errorData = axiosError.response?.data as any;
          const errorCode = errorData?.errorCode;
          let errorMessage = '';

          if (axiosError.code === 'ECONNABORTED') {
            errorMessage = 'İstek zaman aşımına uğradı';
          } else if (axiosError.code === 'ERR_NETWORK') {
            errorMessage = 'Ağ hatası oluştu';
          } else if (errorData) {
            errorMessage = errorData.message || errorData.error || `Hata: ${statusCode}`;
          } else {
            errorMessage = axiosError.message || 'Bilinmeyen hata';
          }

          if (options.errorCallback) {
            errorMessage = options.errorCallback(errorMessage, statusCode);
          }

          return {
            success: false,
            error: errorMessage,
            statusCode: statusCode,
            data: errorData || null,
            errorCode: errorCode
          };
        }
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Bilinmeyen hata',
          statusCode: 0,
          data: null
        };
      }
    } catch (outerError) {
      console.error('[ApiService] Critical error:', outerError);
      return {
        success: false,
        error: 'Internal Error',
        statusCode: 0,
        data: null
      };
    }
  }
}

export default ApiService.getInstance();
